/**
 * Apply one of the farm's bundles (#372): pick it, confirm the pond area (and the typed amount of
 * a proportion-led bundle), see each product's amount, Save. One Save creates the ordinary
 * application rows; the server computes the amounts from the farm's own recipe.
 */
import { todayInPH } from '@/lib/phDate';
import { useEffect, useState } from 'react';
import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useApplicationBundles } from '@/hooks/useApplicationBundles';
import { useFarmProducts } from '@/components/pond/page/data';
import { amountText } from '@/components/pond/page/format';
import { api } from '@/lib/api';
import { saveFailureMessage } from '@/lib/apiErrors';
import { bundleAmounts } from '@/lib/applicationBundles';
import { cn } from '@/lib/cn';
import { useIdempotencyKey } from '@/lib/idempotency';
import { parseTypedAmount } from '@/lib/number';
import { qk } from '@/lib/queryKeys';

interface Props {
  farmId: string;
  cropId: string;
  /** The day the application is for (a past day is a back-fill). */
  date: string;
  /** The pond's area in hectares, when known: the default for the area box. */
  areaHa?: number | string | null;
  /** Phone: bigger targets. */
  phone?: boolean;
  onSaved?: (bundleName: string) => void;
}

const num = (text: string): number | null => {
  const parsed = parseTypedAmount(text);
  return 'value' in parsed ? parsed.value : null;
};

export function BundleApply({ farmId, cropId, date, areaHa, phone, onSaved }: Props) {
  const queryClient = useQueryClient();
  const bundles = useApplicationBundles(farmId);
  const { data: products = [] } = useFarmProducts(farmId);
  const idempotency = useIdempotencyKey();
  const [day, setDay] = useState(date);
  const [bundleId, setBundleId] = useState<string>('');
  const [area, setArea] = useState(areaHa !== null && areaHa !== undefined && Number(areaHa) > 0 ? String(Number(areaHa)) : '');
  const [lead, setLead] = useState('');
  const bundle = bundles.find((b) => b.id === bundleId) ?? null;
  const preview = bundle ? bundleAmounts(bundle, num(area), num(lead)) : null;
  const productOf = (id: string) => products.find((p) => p.id === id);
  const typed = !!bundle?.items[0]?.typed;
  const needsArea = !!bundle?.items.some((i) => i.per_hectare !== undefined);

  // The recipe may have been edited since this screen's cached copy: look again on open (the server
  // always applies the current one, so a stale preview could otherwise show different amounts).
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['farm', farmId] });
  }, [queryClient, farmId]);

  // A save still queued offline survives closing and reopening this screen: count it, so the same
  // bundle can't be applied twice.
  const saveKey = ['bundle-apply', cropId] as const;
  const queuedSaves = useMutationState({ filters: { mutationKey: saveKey, status: 'pending' }, select: (m) => m.state.isPaused });
  const queued = queuedSaves.length > 0;

  const save = useMutation({
    mutationKey: saveKey,
    retry: false,
    mutationFn: () =>
      api.post<{ data: Array<{ product_name: string; amount: string | null; unit: string | null }> }>(`/api/v1/farms/${farmId}/applications/bundle`, {
        cycle_id: cropId,
        application_date: day,
        bundle_id: bundle!.id,
        ...(needsArea ? { area_ha: num(area) } : {}),
        ...(typed ? { lead_amount: num(lead) } : {}),
        idempotency_key: idempotency.keyFor(JSON.stringify([cropId, day, bundle!.id, area, lead])),
      }),
    onSuccess: (result) => {
      idempotency.reset();
      // What the server saved (it computes the amounts from the farm's current recipe).
      const saved = (result.data ?? []).map((r) => `${r.product_name} ${amountText(r.amount, r.unit)}`).join(', ');
      const name = bundle!.name;
      toast.success(`Logged ${name}: ${saved}`, { duration: 6000 });
      // Back to a blank form: Save is off until something is chosen again.
      setBundleId('');
      setLead('');
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      queryClient.invalidateQueries({ queryKey: qk.products.list(farmId) });
      queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
      onSaved?.(name);
    },
  });

  if (bundles.length === 0) return null;
  const field = cn('w-full rounded-lg border border-gray-300 bg-white px-3 text-gray-900', phone ? 'min-h-12 text-base' : 'min-h-[40px] text-sm');
  return (
    <div className="flex flex-col gap-3" data-bundle-apply>
      <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
        Bundle
        <select className={field} value={bundleId} onChange={(e) => setBundleId(e.target.value)} aria-label="Bundle">
          <option value="">Choose a bundle</option>
          {bundles.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </label>
      {bundle && (
        <>
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
            When
            <input type="date" className={field} value={day} max={todayInPH()} onChange={(e) => e.target.value && setDay(e.target.value)} aria-label="Bundle date" />
          </label>
          {needsArea && (
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
              Pond area (ha)
              <input className={cn(field, 'tabular-nums')} inputMode="decimal" value={area} onChange={(e) => setArea(e.target.value)} aria-label="Pond area in hectares" />
            </label>
          )}
          {typed && (
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-gray-700">
              {productOf(bundle.items[0].product_id)?.name ?? 'First product'} ({productOf(bundle.items[0].product_id)?.unit ?? ''})
              <input className={cn(field, 'tabular-nums')} inputMode="decimal" value={lead} onChange={(e) => setLead(e.target.value)} aria-label="Amount of the first product" />
            </label>
          )}
          <div className="rounded-lg bg-gray-50 p-3 text-sm" data-bundle-preview>
            {preview && 'amounts' in preview ? (
              <ul className="space-y-1">
                {preview.amounts.map((a) => (
                  <li key={a.product_id} className="flex items-baseline justify-between gap-3">
                    <span className="text-gray-800">{productOf(a.product_id)?.name ?? 'Product'}</span>
                    <span className="font-semibold tabular-nums">{amountText(a.amount, productOf(a.product_id)?.unit)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500">{preview && 'error' in preview ? preview.error : ''}</p>
            )}
          </div>
          {save.isError && (
            <p role="alert" className="text-sm text-red-700">
              Not saved: {saveFailureMessage(save.error, 'try again')}
            </p>
          )}
          <button
            type="button"
            disabled={!preview || !('amounts' in preview) || save.isPending || queued}
            onClick={() => save.mutate()}
            className={cn('rounded-lg bg-primary-600 px-4 font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50', phone ? 'min-h-12 text-[15px]' : 'min-h-[40px] text-sm')}
          >
            {queued ? 'Waiting for connection…' : save.isPending ? 'Saving…' : `Save ${bundle.name}`}
          </button>
        </>
      )}
    </div>
  );
}
